import styled, { css } from 'styled-components'

type CardProps = {
  color?: string
  textColor?: string
}

export const CardWrapper = styled.div<CardProps>`
  background-color: ${({ color }) => color || 'black'};
  max-width: 26rem;
  min-height: 13.75rem;
  border-radius: 1rem;
  overflow: hidden;
  color: ${({ textColor }) => textColor || 'white'};
  display: flex;
  flex-direction: column;
  position: relative;
  box-shadow:
    rgb(154 160 185 / 25%) 0px 5px 10px,
    rgb(166 173 201 / 40%) 0px 15px 40px;
  width: 100%;

  @media print {
    box-shadow: none;
  }
`

const TopLine = styled.div`
  padding-top: 1.5rem;
`

const TicketSubTitle = styled.div`
  font-size: 0.875rem;
`

const TicketTitle = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
`

const TicketBody = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  flex-wrap: wrap;
`

const TicketNumber = styled.div`
  font-weight: bold;
  font-size: 1rem;
  text-transform: uppercase;
`

const TicketStatus = styled.div`
  padding: 2px 12px;
  border-radius: 12px;
  background-color: ${({ color }) => color};
`

type PositionProps = {
  position: 'top' | 'right'
}

const CardBody = styled.div<PositionProps>`
  position: relative;
  flex: 1;
  width: 100%;
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${({ position }) =>
    position === 'right' &&
    css`
      padding-right: 4.25rem;
    `}
`

type StripeProps = PositionProps & {
  colour: string
}

const Stripe = styled.div<StripeProps>`
  color: ${({ colour }) => colour || 'white'};
  text-transform: uppercase;
  font-weight: bold;
  font-size: 1rem;
  letter-spacing: 2px;
  white-space: nowrap;

  ${({ position }) =>
    position === 'right' &&
    css`
      transform: rotate(270deg);
    `}
`

const StripeWrapper = styled.div<PositionProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ color }) => color};

  ${({ position }) =>
    position === 'top' &&
    css`
      left: 0;
      height: 2rem;
    `}

  ${({ position }) =>
    position === 'right' &&
    css`
      bottom: 0;
      width: 2.5rem;
      position: absolute;
      right: 0;
      top: 0;
    `}
`

const TicketCard = ({
  stripe,
  status,
  color,
  textColor,
  title,
  descriptor,
  number,
  expiry,
  ...rest
}) => {
  return (
    <CardWrapper color={color} textColor={textColor} {...rest}>
      {stripe && (
        <StripeWrapper position={stripe.position} color={stripe.color}>
          <Stripe position={stripe.position} colour={stripe.textColor}>
            {stripe.text}
          </Stripe>
        </StripeWrapper>
      )}
      <CardBody position={stripe?.position}>
        <TopLine>
          {descriptor !== title && (
            <TicketSubTitle>{descriptor}</TicketSubTitle>
          )}
          <TicketTitle>{title}</TicketTitle>
        </TopLine>
        <TicketBody>
          <div>
            {number && <TicketNumber>{number}</TicketNumber>}
            {expiry && <div>Expiry: {expiry}</div>}
          </div>
          {status && (
            <TicketStatus color={status.color}>{status.text}</TicketStatus>
          )}
        </TicketBody>
      </CardBody>
    </CardWrapper>
  )
}

export default TicketCard
